import { useEffect, useMemo, useState } from 'react';
import {
	api,
	deleteEmails,
	get,
	search,
} from '@/api/submission-scheduler/emails';
import { Filter } from '@/api/submission-scheduler/filter';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/ui/custom/data-table';
import { TablePagination } from '@/components/ui/custom/table-pagination';
import { useDebounce } from '@/hooks/use-debounce';
import { useExportCsvWithObjParam } from '@/hooks/use-export-csv-with-obj-param';
import {
	keepPreviousData,
	Updater,
	useQuery,
	useQueryClient,
} from '@tanstack/react-query';
import {
	ColumnFiltersState,
	getCoreRowModel,
	getFilteredRowModel,
	getPaginationRowModel,
	getSortedRowModel,
	PaginationState,
	SortingState,
	useReactTable,
	VisibilityState,
} from '@tanstack/react-table';

import { columns } from './columns';
import { DeleteDialog } from './delete-dialog';

export function SubmissionSchedulerTable() {
	const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
	const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
	const [rowSelection, setRowSelection] = useState({});
	const [hasInteracted, setHasInteracted] = useState(false);
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);
	const debouncedColumnFilters = useDebounce(columnFilters, 500);
	const queryClient = useQueryClient();

	const [pagination, setPagination] = useState<PaginationState>({
		pageIndex: 0,
		pageSize: 10,
	});
	const [sorting, setSorting] = useState<SortingState>([
		{
			id: 'deliveryDateTime',
			desc: true,
		},
	]);
	const { data, isFetching } = useQuery({
		queryFn: () => {
			// Use search if user has interacted with table
			if (hasInteracted || debouncedColumnFilters.length > 0) {
				return search(apiFilter, pagination, sorting);
			}
			// Use get only on initial load
			return get();
		},
		queryKey: [
			'submission-scheduler',
			hasInteracted,
			pagination,
			sorting,
			debouncedColumnFilters,
		],
		placeholderData: keepPreviousData,
	});

	const apiFilter = useMemo<Filter>(() => {
		const filterObj: Partial<Filter> = {
			sender: '',
		};

		debouncedColumnFilters.forEach((filter) => {
			const value = filter.value as string;
			switch (filter.id) {
				case 'sender':
					filterObj.sender = value || '';
					break;
				case 'recipient':
					filterObj.recipient = value || null;
					break;
				case 'subject':
					filterObj.subject = value || null;
					break;
				case 'deliveryDateTime':
					filterObj.deliveryDate = value || null;
					break;
				case 'status':
					filterObj.status = value || null;
					break;
			}
		});

		return filterObj as Filter;
	}, [debouncedColumnFilters]);

	useEffect(() => {
		setPagination((prev) => ({ ...prev, pageIndex: 0 }));
	}, [debouncedColumnFilters]);

	useEffect(() => {
		if (
			debouncedColumnFilters.length > 0 ||
			pagination.pageIndex > 0 ||
			pagination.pageSize !== 10 ||
			sorting[0]?.id !== 'deliveryDateTime' ||
			sorting[0]?.desc !== true
		) {
			setHasInteracted(true);
		}
	}, [debouncedColumnFilters, pagination, sorting]);

	const handlePageChange = (
		updater: Updater<PaginationState, PaginationState>,
	) => {
		setPagination((prev) => {
			const newPagination =
				typeof updater === 'function' ? updater(prev) : updater;

			return newPagination;
		});
	};

	const { exportCsv, isExporting } = useExportCsvWithObjParam();

	const table = useReactTable({
		data: data?.data ?? [],
		columns: columns,
		rowCount: data?.total ?? 0,
		getRowId: (row) => row.id.toString(),
		manualPagination: true,
		manualSorting: true,
		manualFiltering: true,
		onSortingChange: setSorting,
		onColumnFiltersChange: setColumnFilters,
		getCoreRowModel: getCoreRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		onColumnVisibilityChange: setColumnVisibility,
		onRowSelectionChange: setRowSelection,
		onPaginationChange: handlePageChange,
		state: {
			sorting,
			columnFilters,
			columnVisibility,
			rowSelection,
			pagination,
		},
	});

	const selectedEmailIds = useMemo(() => {
		return table.getSelectedRowModel().rows.map((row) => row.original.id);
	}, [table.getSelectedRowModel().rows]);

	const handleDelete = async () => {
		setIsDeleting(true);
		try {
			const success = await deleteEmails(selectedEmailIds);
			if (success) {
				// Invalidate query to refresh data
				queryClient.invalidateQueries({ queryKey: ['submission-scheduler'] });
				// Clear selection
				table.resetRowSelection();
				setDeleteDialogOpen(false);
			} else {
				// Handle error (could add toast notification)
				console.error('Failed to delete emails');
			}
		} catch (error) {
			console.error('Error deleting emails:', error);
		} finally {
			setIsDeleting(false);
		}
	};

	return (
		<div className="h-1/2 w-full">
			<div className="mb-4 flex gap-2">
				<Button
					disabled={isExporting}
					onClick={() => {
						exportCsv(`${api}/export`, {
							filter: apiFilter,
							pageIndex: 0,
							pageSize: 0,
							sortBy: sorting[0]?.id || 'deliveryDateTime',
							sortDirection: sorting[0]?.desc ? 'desc' : 'asc',
						});
					}}
					variant="outline"
				>
					Export
				</Button>

				<Button
					disabled={selectedEmailIds.length === 0 || isDeleting}
					onClick={() => setDeleteDialogOpen(true)}
					variant="destructive"
				>
					Delete ({selectedEmailIds.length})
				</Button>
			</div>

			<DataTable
				table={table}
				isFetching={isFetching}
				noResultsMessage="No emails found."
			/>

			<TablePagination table={table} isFetching={isFetching} />

			<DeleteDialog
				open={deleteDialogOpen}
				onOpenChange={setDeleteDialogOpen}
				onConfirm={handleDelete}
				emailCount={selectedEmailIds.length}
				isDeleting={isDeleting}
			/>
		</div>
	);
}
